import { toLiteralType } from "src/utils/types";
import { UUID } from "src/utils/uuid";

export const NAMESPACE = "team";
export type TeamID = UUID<typeof NAMESPACE>;
export const toTeamID = (str: string) => str as TeamID;

export type PlayerStatus = "active" | "merged" | "removed";

export type SeasonAndYear = {
  season: Season;
  year: number;
};

export enum ValidSport {
  BASEBALL = "baseball",
  SOFTBALL = "softball",
}
export const toSport = (str: string) => toLiteralType(str, "Sport", Object.values(Sport));

export enum InvalidSport {
  OTHER = "other",
}

export const Sport = { ...InvalidSport, ...ValidSport };
// eslint-disable-next-line
export type Sport = InvalidSport | ValidSport;

export enum AgeDiscrete {
  EIGHT_AND_UNDER = "8U",
  NINE_AND_UNDER = "9U",
  TEN_AND_UNDER = "10U",
  ELEVEN_AND_UNDER = "11U",
  TWELVE_AND_UNDER = "12U",
  THIRTEEN_AND_UNDER = "13U",
  FOURTEEN_AND_UNDER = "14U",
  FIFTEEN_AND_UNDER = "15U",
  SIXTEEN_AND_UNDER = "16U",
  SEVENTEEN_AND_UNDER = "17U",
  EIGHTEEN_AND_UNDER = "18U",
  EIGHTEEN_AND_OVER = "18O",
}

export enum AgeRange {
  UNDER_THIRTEEN = "Under 13",
  THIRTEEN_TO_EIGHTEEN = "Between 13 - 18",
  OVER_EIGHTEEN = "Over 18",
}

export enum AgeSchoolLevel {
  ELEMENTARY = "elementary",
  MIDDLE_TWELVE_AND_UNDER = "middle_12U",
  MIDDLE_THIRTEEN_AND_OVER = "middle_13O",
  HIGH_FRESHMAN = "high_freshman",
  HIGH_JUNIOR_VARSITY = "high_junior_varsity",
  HIGH_VARSITY = "high_varsity",
  COLLEGE = "college",
}

export const AgeSchoolOptions = {
  HIGH_SCHOOL: "high_school",
  MIDDLE_SCHOOL: "middle_school",
  ELEMENTARY: AgeSchoolLevel.ELEMENTARY,
  COLLEGE: AgeSchoolLevel.COLLEGE,
} as const;

export type AgeSchoolOptionType = (typeof AgeSchoolOptions)[keyof typeof AgeSchoolOptions];

export type AgeGroup = AgeDiscrete | AgeRange | AgeSchoolLevel;

export enum CompetitionLevel {
  RECREATIONAL = "recreational",
  CLUB_TRAVEL = "club_travel",
  SCHOOL = "school",
}

export enum Season {
  SPRING = "spring",
  SUMMER = "summer",
  FALL = "fall",
  WINTER = "winter",
}
export const toSeason = (str: string) => toLiteralType(str, "season", Object.values(Season));

export enum TeamType {
  ADMIN = "admin",
  OPPONENT = "opponent",
}

export interface Record {
  wins: number;
  losses: number;
  ties: number;
}

export interface Team {
  id: TeamID;
  name: string;
  sport: Sport;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  ageGroup: AgeGroup;
  seasonName: Season;
  seasonYear: number;
  statAccessLevel?: string | null;
  scorekeepingAccessLevel?: string | null;
  streamingAccessLevel?: string | null;
  competitionLevel: CompetitionLevel;
  teamType: TeamType;
  inningsPerGame?: number;
  seasonRecord: string;
  staff: string[] | "No Staff Found";
  // ISO timestamp of the backend's last sync from GameChanger; for a "last updated" UI label.
  lastUpdated?: string | null;
}

export interface PlayerIdAndName {
  id?: string;
  teamID?: TeamID;
  name: string;
  number: string;
  firstName?: string;
  lastName?: string;
  status?: PlayerStatus;
}

export type CoreBattingStats = {
  "1B": number;
  "2B": number;
  "2OUTRBI": number;
  "2S+3%": number;
  "2S+3": number;
  "3B": number;
  "6+%": number;
  "6+": number;
  AB: number;
  "AB/HR": number;
  AVG: number;
  "BA/RISP": number;
  BABIP: number;
  BB: number;
  "BB/K": number;
  "C%": number;
  CI: number;
  CS: number;
  "FB%": number;
  FC: number;
  "GB%": number;
  GIDP: number;
  GITP: number;
  GP: number;
  H: number;
  HBP: number;
  HHB: number;
  HR: number;
  "K-L": number;
  "LD%": number;
  LOB: number;
  OBP: number;
  OPS: number;
  PA: number;
  "PA/BB": number;
  PIK: number;
  PS: number;
  "PS/PA": number;
  QAB: number;
  "QAB%": number;
  R: number;
  RBI: number;
  ROE: number;
  SAC: number;
  SB: number;
  "SB%": number;
  SF: number;
  SLG: number;
  SO: number;
  TB: number;
  XBH: number;
  LD: number;
  GB: number;
  FB: number;
};
export type TeamBattingStats = CoreBattingStats & { player: PlayerIdAndName };
export type EventBattingStats = CoreBattingStats & { event: string; opponent: string };

export type CorePitchingStats = {
  IP: number;
  "GP:P": number;
  GS: number;
  BF: number;
  "#P": number;
  W: number;
  L: number;
  SV: number;
  SVO: number;
  BS: number;
  "SV%": number;
  H: number;
  R: number;
  ER: number;
  BB: number;
  SO: number;
  "K-L": number;
  HBP: number;
  ERA: number;
  WHIP: number;
  LOB: number;
  BK: number;
  PIK: number;
  CS: number;
  SB: number;
  "SB%": number;
  WP: number;
  BAA: number;
  TB: number;
  TS: number;
  "P/IP": number;
  "P/BF": number;
  "LBFP#": string | number;
  "<3%": number;
  LOO: number;
  "1ST2OUT": number;
  "123INN": number;
  "<13": number;
  FIP: number;
  "S%": number;
  "FPS%": number;
  "FPSO%": number;
  "FPSW%": number;
  "FPSH%": number;
  "BB/INN": number;
  "0BBINN": number;
  BBS: number;
  LOBB: number;
  LOBBS: number;
  "SM%": number;
  "K/BF": number;
  "K/BB": number;
  "WEAK%": number;
  "HHB%": number;
  "GO/AO": number;
  HR: number;
  "LD%": number;
  "FB%": number;
  "GB%": number;
  BABIP: number;
  "BA/RISP": number;
  FB: number;
  FBS: number;
  "FBS%": number;
  "FBSW%": number;
  "FBSM%": number;
  CT: number;
  CTS: number;
  "CTS%": number;
  "CTSW%": number;
  "CTSM%": number;
  CB: number;
  CBS: number;
  "CBS%": number;
  "CBSW%": number;
  "CBSM%": number;
  SL: number;
  SLS: number;
  "SLS%": number;
  "SLSW%": number;
  "SLSM%": number;
  CH: number;
  CHS: number;
  "CHS%": number;
  "CHSW%": number;
  "CHSM%": number;
  OS: number;
  OSS: number;
  "OSS%": number;
  "OSSW%": number;
  "OSSM%": number;
  RB: number;
  RBS: number;
  "RBS%": number;
  "RBSW%": number;
  "RBSM%": number;
  DB: number;
  DBS: number;
  "DBS%": number;
  "DBSW%": number;
  "DBSM%": number;
  SC: number;
  SCS: number;
  "SCS%": number;
  "SCSW%": number;
  "SCSM%": number;
  DC: number;
  DCS: number;
  "DCS%": number;
  "DCSW%": number;
  "DCSM%": number;
  KB: number;
  KBS: number;
  "KBS%": number;
  "KBSW%": number;
  "KBSM%": number;
  KC: number;
  KCS: number;
  "KCS%": number;
  "KCSW%": number;
  "KCSM%": number;
  "<3": number;
  SM: number;
  SW: number;
  WHB: number;
  HHB: number;
  GO: number;
  AO: number;
  LD: number;
  GB: number;
  FLB: number;
};
export type TeamPitchingStats = CorePitchingStats & { player: PlayerIdAndName };
export type EventPitchingStats = CorePitchingStats & { event: string; opponent: string };

export type CoreFieldingStats = {
  TC: number;
  A: number;
  PO: number;
  FPCT: number;
  E: number;
  DP: number;
  TP: number;
  "GP:F": number;
  INN: number;
  PB: number;
  SB: number;
  "SB-ATT": string;
  CS: number;
  "CS%": number;
  PIK: number;
  CI: number;
  "GP:C": number;
};
export type TeamFieldingStats = CoreFieldingStats & { player: PlayerIdAndName };
export type EventFieldingStats = CoreFieldingStats & { event: string; opponent: string };

export type PlayerStats = TeamBattingStats | TeamPitchingStats | TeamFieldingStats;

export type TeamStats = BaseballStats | SoftballStats;
export type TeamEventStats = BaseballEventStats;

export type BaseballStats = {
  batting: {
    playerStats: TeamBattingStats[];
    teamStats: TeamBattingStats[];
  };
  pitching: {
    playerStats: TeamPitchingStats[];
    teamStats: TeamPitchingStats[];
  };
  fielding: {
    playerCatchingStats: TeamFieldingStats[];
    playerStats: TeamFieldingStats[];
    teamStats: TeamFieldingStats[];
  };
};
export type SoftballStats = BaseballStats;
export type BaseballEventStats = {
  batting: {
    playerStats: EventBattingStats[];
    cumulativeStats: EventBattingStats[];
  };
  pitching: {
    playerStats: EventPitchingStats[];
    cumulativeStats: EventPitchingStats[];
  };
  fielding: {
    playerCatchingStats: EventFieldingStats[];
    cumulativeCatchingStats: EventFieldingStats[];
    playerStats: EventFieldingStats[];
    cumulativeStats: EventFieldingStats[];
  };
  offensiveSprayCharts: SprayChartData[];
  defensiveSprayCharts: SprayChartData[];
};

export type PlayResult = "hit" | "out";

export enum HomeRunLocation {
  LEFT_FIELD = "left_field",
  CENTER_FIELD = "center_field",
  RIGHT_FIELD = "right_field",
  IN_THE_PARK = "in_the_park",
}

export type SprayChartData = {
  id: string;
  code: string;
  location: {
    x: number;
    y: number;
  };
  playResult: PlayResult;
  hrLocation?: HomeRunLocation;
};

export const isAgeGroup13U = (ageGroup: AgeGroup): boolean => {
  return (
    ageGroup === AgeDiscrete.EIGHT_AND_UNDER ||
    ageGroup === AgeDiscrete.NINE_AND_UNDER ||
    ageGroup === AgeDiscrete.TEN_AND_UNDER ||
    ageGroup === AgeDiscrete.ELEVEN_AND_UNDER ||
    ageGroup === AgeDiscrete.TWELVE_AND_UNDER ||
    ageGroup === AgeDiscrete.THIRTEEN_AND_UNDER ||
    ageGroup === AgeRange.UNDER_THIRTEEN ||
    ageGroup === AgeSchoolLevel.MIDDLE_TWELVE_AND_UNDER ||
    ageGroup === AgeSchoolLevel.ELEMENTARY
  );
};

type BaseListTeam = {
  name: string;
  sport: Sport;
  seasonName: Season;
  seasonYear: number;
  statLevel: string;
  displaySeason: string;
};

export type ListTeam = BaseListTeam & {
  id: TeamID;
  playerCount: number;
  seasonRecord: Record;
  // ISO timestamp of the backend's last sync from GameChanger; for a "last updated" UI label.
  lastUpdated?: string | null;
};

export const isListTeam = (x: ListTeam): x is ListTeam => "teamGroups" in x;
export const isAdminOfListItem = (item: ListTeam) => {
  return true;
};

export interface TeamListGroupedBySeason {
  [season: string]: ListTeam[];
}

export interface TeamSearch {
  sport: string;
  name: string;
  seasons: SearchSeason[];
  states: string[];
}

export interface SearchSeason {
  name: string;
  year: string;
}

export interface Player {
  id: string;
  batting_side: string;
  first_name: string;
  last_name: string;
  number: number;
  person_id: string;
  status: string;
  team_id: string;
  throwing_hand: string;
}

export interface Players {
  [key: string]: {
    stats: StatData;
  };
}

export type Defense = {
  ip: number;
  "GP:P": number;
  gs: number;
  bf: number;
  "#P": number;
  w: number;
  l: number;
  sv: number;
  svo: number;
  bs: number;
  "SV%": number;
  h: number;
  r: number;
  er: number;
  bb: number;
  so: number;
  sol: number;
  hbp: number;
  era: number;
  whip: number;
  lob: number;
  bk: number;
  "SB%": number;
  wp: number;
  baa: number;
  tb: number;
  ts: number;
  "P/IP": number;
  "P/BF": number;
  "LBFP#": string | number;
  "<3%": number;
  loo: number;
  "1ST2OUT": number;
  "123INN": number;
  "<13": number;
  fip: number;
  "S%": number;
  "FLB%": number;
  "FPS%": number;
  "FPSO%": number;
  "FPSW%": number;
  "FPSH%": number;
  "BB/INN": number;
  "0BBINN": number;
  "IC:C": number;
  "PB:C": number;
  "SB:C": number;
  "SBATT:C": number;
  "PIK:C": number;
  "CS:C": number;
  "CI:C": number;
  "CS:C%": number;
  bbs: number;
  lobb: number;
  lobbs: number;
  "SM%": number;
  "K/BF": number;
  "K/BB": number;
  "WEAK%": number;
  "HARD%": number;
  "GO/AO": number;
  hr: number;
  "LND%": number;
  "FB%": number;
  "GB%": number;
  babip: number;
  "BA/RISP": number;
  FB: number;
  FBS: number;
  "FBS%": number;
  "FBSW%": number;
  "FBSM%": number;
  CT: number;
  CTS: number;
  "CTS%": number;
  "CTSW%": number;
  "CTSM%": number;
  CB: number;
  CBS: number;
  "CBS%": number;
  "CBSW%": number;
  "CBSM%": number;
  SL: number;
  SLS: number;
  "SLS%": number;
  "SLSW%": number;
  "SLSM%": number;
  CH: number;
  CHS: number;
  "CHS%": number;
  "CHSW%": number;
  "CHSM%": number;
  OS: number;
  OSS: number;
  "OSS%": number;
  "OSSW%": number;
  "OSSM%": number;
  RB: number;
  RBS: number;
  "RBS%": number;
  "RBSW%": number;
  "RBSM%": number;
  DB: number;
  DBS: number;
  "DBS%": number;
  "DBSW%": number;
  "DBSM%": number;
  SC: number;
  SCS: number;
  "SCS%": number;
  "SCSW%": number;
  "SCSM%": number;
  DC: number;
  DCS: number;
  "DCS%": number;
  "DCSW%": number;
  "DCSM%": number;
  KB: number;
  KBS: number;
  "KBS%": number;
  "KBSW%": number;
  "KBSM%": number;
  KC: number;
  KCS: number;
  "KCS%": number;
  "KCSW%": number;
  "KCSM%": number;
  "<3": number;
  SM: number;
  SW: number;
  WHB: number;
  hhb: number;
  GO: number;
  AO: number;
  LD: number;
  GB: number;
  FLB: number;
  tc: number;
  a: number;
  po: number;
  fpct: number;
  e: number;
  dp: number;
  tp: number;
  "GP:F": number;
  INN: number;
  pb: number;
  sb: number;
  "SB-ATT": string;
  cs: number;
  "CS%": number;
  pik: number;
  ci: number;
  "GP:C": number;
  "IP:1B": number;
  "IP:2B": number;
  "IP:3B": number;
  "IP:CF": number;
  "IP:F": number;
  "IP:LF": number;
  "IP:RF": number;
  "IP:SF": number;
  "IP:SS": number;
};

export type StatData = {
  defense: Defense;
  general: {
    gp: number | null;
  };
  offense: CoreBattingStats;
};

/** One day of pitch counts keyed by player id; `pitchSmart[d]` is `d` days ago. */
export interface PitchSmartData {
  [playerId: string]: number;
}

export interface SeasonStats {
  id: string;
  team_id: string;
  stats_data: {
    players: Players;
    stats: StatData;
  };
}

export interface PitchSmart {
  players: Player[];
  pitchSmart: PitchSmartData[];
  seasonStats: SeasonStats;
}

export interface VideoAsset {
  id: string;
  stream_id: string;
  duration: number;
  schedule_event_id: string;
  created_at: string;
  ended_at: string;
  audience_type: string;
  thumbnail_url: string;
}

export interface Opponent {
  root_team_id: string;
  owning_team_id: string;
  progenitor_team_id: string;
  name: string;
  is_hidden: boolean;
}
